import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PaymentModal from '../components/PaymentModal';
import styles from './PricingPage.module.css';
import { PRICING_PLANS } from '../data/pricingPlans';
import seedIcon from '../assets/icons/seed.png';
import rootIcon from '../assets/icons/root.png';
import bloomIcon from '../assets/icons/bloom.png';
import thriveIcon from '../assets/icons/thrive.png';

const TABLE_ROWS = [
  {
    label: 'Pricing',
    key: 'pricing',
    values: {
      Seed: { type: 'price', text: 'Free' },
      Root: { type: 'price', text: '\u20b931,239' },
      Bloom: { type: 'price', text: '\u20b969,399' },
      Thrive: { type: 'price', text: '\u20b91,19,799' },
    },
  },
  {
    label: 'Core Features',
    key: 'coreFeatures',
    values: {
      Seed: { type: 'text', text: 'Free Onboarding' },
      Root: { type: 'text', text: 'Free onboarding + Commission Discount' },
      Bloom: { type: 'text', text: 'All Core Features Of Root + All Flagship Features' },
      Thrive: { type: 'text', text: 'All Core Features Of Bloom + Zero Commission On Sales!' },
    },
  },
  {
    label: 'Term Option*',
    key: 'termOption',
    values: {
      Seed: { type: 'text', text: 'Lifetime' },
      Root: { type: 'text', text: '4 months' },
      Bloom: { type: 'text', text: '8 months' },
      Thrive: { type: 'text', text: 'Annual' },
    },
  },
  {
    label: 'Commission**',
    key: 'commission',
    values: {
      Seed: { type: 'text', text: '25%' },
      Root: { type: 'text', text: '15%' },
      Bloom: { type: 'text', text: '10%' },
      Thrive: { type: 'text', text: '0%' },
    },
  },
  {
    label: 'Growth Credits',
    key: 'growthCredits',
    values: {
      Seed: { type: 'credits', base: '0', complimentary: '18' },
      Root: { type: 'credits', base: '250', complimentary: '30' },
      Bloom: { type: 'credits', base: '500', complimentary: '80' },
      Thrive: { type: 'credits', base: '750', complimentary: '230' },
    },
  },
  {
    label: 'Shipping Credit',
    key: 'shippingCredit',
    values: {
      Seed: { type: 'text', text: 'At Actual' },
      Root: { type: 'text', text: 'At Actual' },
      Bloom: { type: 'text', text: 'At Actual' },
      Thrive: { type: 'text', text: '59 to 99*' },
    },
  },
  {
    label: 'Total Value',
    key: 'totalValue',
    values: {
      Seed: { type: 'total', text: '\u20b90' },
      Root: { type: 'total', text: '\u20b935,000' },
      Bloom: { type: 'total', text: '\u20b972,500' },
      Thrive: { type: 'total', text: '\u20b91,22,500' },
    },
  },
];

const PLAN_BADGE_DATA = {
  Seed: { text: 'Basic Plan', flagship: false },
  Root: { text: 'Essential Plan', flagship: false },
  Bloom: { text: 'Advanced Plan', flagship: false },
  Thrive: { text: 'Flagship Plan', flagship: true },
};

const PLAN_TAGLINES = {
  Seed: 'Start free, explore the platform, and grow at your own pace.',
  Root: 'Build early traction with visibility, credibility, and essential growth tools.',
  Bloom: 'Amplify your presence with performance-driven marketing and creative support.',
  Thrive: 'Experience Suspire at its fullest, 0% commission, top-tier access, bold growth.',
};

const ROW_BG_MAP = {
  pricing: 'bgRowPink',
  coreFeatures: 'bgRow3',
  termOption: '',
  commission: 'bgRow5',
  growthCredits: '',
  shippingCredit: 'bgRow7',
  totalValue: 'bgRow9',
};

function PricingPage() {
  const PLAN_ICONS = { Seed: seedIcon, Root: rootIcon, Bloom: bloomIcon, Thrive: thriveIcon };

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [userBrandId, setUserBrandId] = useState(null);
  const [userBrand, setUserBrand] = useState(null);
  // priveModal: null | { status: 'confirm'|'loading'|'success'|'error' }
  const [priveModal, setPriveModal] = useState(null);
  // Deck uploaded from Django admin; falls back to the PDF bundled in /public
  const [partnerDeck, setPartnerDeck] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetch(process.env.REACT_APP_API_BASE_URL + '/api/auth/users/', {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(res => (res.ok ? res.json() : null))
        .then(data => {
          if (data?.user?.brand) {
            setUserBrand(data.user.brand);
            setUserBrandId(data.user.brand.id);
          }
        })
        .catch(err => console.error('Error fetching user profile:', err));
    }
  }, []);

  useEffect(() => {
    fetch(process.env.REACT_APP_API_BASE_URL + '/api/subscriptions/partner-deck/')
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (data?.file_url) setPartnerDeck(data);
      })
      .catch(err => console.error('Error fetching partner deck:', err));
  }, []);

  const handleDownloadDeck = () => {
    if (!partnerDeck?.download_url) return;

    // The API streams the deck with Content-Disposition: attachment, so the browser
    // downloads it even though the API is a different origin.
    const link = document.createElement('a');
    link.href = partnerDeck.download_url;
    link.download = partnerDeck.file_name || 'Suspire Brand Partnership Deck.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getCurrentPlanName = () => userBrand?.subscription_plan?.name || null;
  const isCurrentPlan = planName => getCurrentPlanName() === planName;

  const isPlanDisabled = plan => {
    const currentPlanName = getCurrentPlanName();
    if (!currentPlanName) return false;
    if (isCurrentPlan(plan.name)) return true;
    const hierarchy = ['Seed', 'Root', 'Bloom', 'Thrive'];
    return hierarchy.indexOf(plan.name) < hierarchy.indexOf(currentPlanName);
  };

  const handleChoosePlan = plan => {
    if (!isPlanDisabled(plan)) {
      setSelectedPlan(plan);
      setShowPaymentModal(true);
    }
  };

  const handlePriveRequest = () => {
    setPriveModal({ status: 'confirm' });
  };

  const handleSubmitPriveRequest = async () => {
    setPriveModal({ status: 'loading' });
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(process.env.REACT_APP_API_BASE_URL + '/api/subscriptions/request-upgrade/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          request_type: 'prive',
          brand_name: userBrand?.name || 'Unknown Brand',
          brand_id: userBrandId,
        }),
      });
      if (!res.ok) throw new Error('failed');
      setPriveModal({ status: 'success' });
    } catch {
      setPriveModal({ status: 'error' });
    }
  };

  const closePriveModal = () => setPriveModal(null);

  const renderCellValue = (cell, rowKey) => {
    if (!cell) return null;
    if (cell.type === 'price') {
      const isFree = cell.text === 'Free';
      if (isFree) return <span className={styles.priceFree}>{cell.text}</span>;
      // Label clarifies this is the total for the plan's full term (see the
      // "Term Option" row), which is why it differs from the per-month amount
      // shown in the checkout modal.
      return (
        <span className={styles.pricePaid}>
          {cell.text}
          <span className={styles.priceCaption}>for full plan term</span>
        </span>
      );
    }
    if (cell.type === 'credits') return (
      <div className={styles.valueWithBadge}>
        <span className={styles.valueText}>{cell.base}</span>
        <span className={styles.plusSign}>+</span>
        <div className={styles.compBadgeWrapper}>
          <span className={styles.compText}>complimentary</span>
          <span className={styles.compNum}>{cell.complimentary}</span>
        </div>
      </div>
    );
    if (cell.type === 'total') return <span className={styles.priceTotal}>{cell.text}</span>;
    if (rowKey === 'coreFeatures') return <span className={styles.featureText}>{cell.text}</span>;
    return <span className={styles.valueText}>{cell.text}</span>;
  };

  return (
    <div className={styles.pricingPage}>
      {/* ===== HEADER ===== */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <span className={styles.titleMain}>Introducing </span>
            <span className={styles.titleHighlight}>Flexi-Ability For You</span>
          </h1>
          <p className={styles.subtitle}>
            Compare our four strategic plans side by side and find the one built for your journey.
          </p>
        </div>
        <div className={styles.headerRight}>
          <button
            type="button"
            className={styles.partnerDeckBtn}
            onClick={handleDownloadDeck}
            disabled={!partnerDeck?.download_url}
            title={
              partnerDeck?.download_url
                ? 'Download the Brand Partner Deck'
                : 'Deck coming soon'
            }
          >
            Brand Partner Deck{' '}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D2613B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </button>
        </div>
      </div>

      {/* ===== PRICING TABLE (CSS Grid) ===== */}
      <div className={styles.tableResponsiveWrapper}>
        <div className={styles.pricingTable}>
          <div className={styles.leftColBg} />

          {/* Header row */}
          <div className={`${styles.cell} ${styles.labelCol} ${styles.headerLabel}`}>
            <h2 className={styles.headerH2}>Plans &amp;<br /><span className={styles.textOrange}>Pricing</span></h2>
          </div>
          {PRICING_PLANS.map(plan => (
            <div key={plan.id} className={`${styles.cell} ${styles.bgRow1}`}>
              <div className={styles.planHeaderTop}>
                <img src={PLAN_ICONS[plan.name]} alt={`${plan.name} Icon`} className={styles.planIcon} />
                <h3 className={styles.planTitle}>{plan.name}</h3>
              </div>
              <span className={`${styles.badge} ${plan.name === 'Thrive' ? styles.badgeDark : styles.badgeGreen}`}>
                {PLAN_BADGE_DATA[plan.name].text}
              </span>
              <p className={styles.planDesc}>{PLAN_TAGLINES[plan.name]}</p>
            </div>
          ))}

          {/* Data rows */}
          {TABLE_ROWS.map(row => (
            <React.Fragment key={row.key}>
              <div className={`${styles.cell} ${styles.labelCol} ${ROW_BG_MAP[row.key] ? styles[ROW_BG_MAP[row.key] + 'Label'] : ''}`}>
                {row.label}
              </div>
              {PRICING_PLANS.map(plan => (
                <div key={plan.id} className={`${styles.cell} ${ROW_BG_MAP[row.key] ? styles[ROW_BG_MAP[row.key]] : ''}`}>
                  {renderCellValue(row.values[plan.name], row.key)}
                </div>
              ))}
            </React.Fragment>
          ))}

          {/* Button row */}
          <div className={`${styles.cell} ${styles.labelCol} ${styles.rowFooter}`} />
          {PRICING_PLANS.map(plan => {
            const isCurrent = isCurrentPlan(plan.name);
            const disabled = isPlanDisabled(plan);
            return (
              <div key={plan.id} className={`${styles.cell} ${styles.rowFooter}`}>
                <button
                  className={isCurrent ? styles.btnOutline : styles.btnSolid}
                  disabled={disabled}
                  onClick={() => handleChoosePlan(plan)}
                >
                  {isCurrent ? 'Your Current Plan' : 'Choose Plan'}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== FOOTER NOTES ===== */}
      <div className={styles.footerNotes}>
        <p className={styles.footerNote}>*basis product Category</p>
        <p className={styles.footerNote}>**we charge 5% transaction fee across all plans</p>
        <p className={styles.footerNoteGreen}>*complimentary credits can only be used on onsite growth suite activities.</p>
      </div>

      {/* ===== SUSPIRE'S PRIVE ===== */}
      <div className={styles.priveSection}>
        <img
          src="/images/suspire-prive-section.svg"
          alt="Suspire's Prive"
          className={styles.priveSectionImg}
        />
        <button type="button" className={styles.feasibilityBtn} onClick={handlePriveRequest} disabled={!userBrandId}>Request a feasibility call</button>
      </div>

      {showPaymentModal && selectedPlan && userBrandId && ReactDOM.createPortal(
        <PaymentModal
          plan={selectedPlan}
          brandId={userBrandId}
          onClose={() => { setShowPaymentModal(false); setSelectedPlan(null); }}
          onSuccess={() => { setShowPaymentModal(false); setSelectedPlan(null); window.location.reload(); }}
        />,
        document.body
      )}

      {priveModal && ReactDOM.createPortal(
        <div className={styles.modalOverlay} onClick={closePriveModal}>
          <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
            {priveModal.status === 'success' ? (
              <>
                <div className={styles.modalIconSuccess}>✓</div>
                <h3 className={styles.modalTitle}>Request Sent!</h3>
                <p className={styles.modalMsg}>The Suspire team will reach out to you shortly.</p>
                <button className={styles.btnSolid} style={{ width: 140 }} onClick={closePriveModal}>Done</button>
              </>
            ) : priveModal.status === 'error' ? (
              <>
                <div className={styles.modalIconError}>!</div>
                <h3 className={styles.modalTitle}>Something went wrong</h3>
                <p className={styles.modalMsg}>Please try again or contact us at info@suspire.in</p>
                <div className={styles.modalActions}>
                  <button className={styles.btnOutline} style={{ width: 110 }} onClick={closePriveModal}>Cancel</button>
                  <button className={styles.btnSolid} style={{ width: 110 }} onClick={handleSubmitPriveRequest}>Retry</button>
                </div>
              </>
            ) : (
              <>
                <h3 className={styles.modalTitle}>Request Prive Access</h3>
                <p className={styles.modalMsg}>
                  Send a Prive request to the Suspire team? They'll reach out to discuss feasibility.
                </p>
                <div className={styles.modalActions}>
                  <button className={styles.btnOutline} style={{ width: 110 }} onClick={closePriveModal}>Cancel</button>
                  <button
                    className={styles.btnSolid}
                    style={{ width: 140 }}
                    onClick={handleSubmitPriveRequest}
                    disabled={priveModal.status === 'loading'}
                  >
                    {priveModal.status === 'loading' ? 'Sending…' : 'Send Request'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default PricingPage;
